export const CHECKOUT_STEPS = [
  { id: 1, label: "Service" },
  { id: 2, label: "Documents" },
  { id: 3, label: "Options" },
  { id: 4, label: "Payment" },
];

// NOTE: Step Two Documents

export const TRANSLATION_OPTIONS = {
  certified: "certified",
  standard: "standard",
} as const;

export const TRANSLATION_OPTION_VALUES = Object.values(TRANSLATION_OPTIONS);

export const TRANSLATION_RATES = {
  certified: 24.5,
  standard: 0.1,
} as const;

// NOTE: Step Three Options

export const TRANSITION_SPEED = {
  standard: "standard",
  express: "express",
} as const;

export const DELIVERY_OPTIONS_STANDARD = {
  expeditedDelivery: "expeditedDelivery",
  desktopPublishing: "desktopPublishing",
  proofreading: "proofreading",
} as const;

// Standard tier is billed per word (matches translation tier convention
// $0.10/word). Add-on rates are calibrated so that a 250-word doc — one
// "standard page" — bills the same as the legacy per-page math: $10 for
// Expedited / DTP and $12.50 for Proofreading. Past 250 words it's pure
// per-word (no ceil() rounding penalty).
// PROD-2026-05-18-002.
export const DELIVERY_RATES_STANDARD = {
  expeditedDelivery: 0.04,
  desktopPublishing: 0.04,
  proofreading: 0.05,
} as const;

// Standard tier floors (per PROD-2026-05-18-002 + PROD-2026-05-18-003).
// Translation: 1 standard page (250 words) at $0.10/word = $25.
// Add-ons: flat $10 minimum per add-on (matches competitor behavior).
export const STANDARD_TRANSLATION_MIN_USD = 25;
export const STANDARD_ADDON_MIN_USD = 10;

// Trust signals surfaced in the Order Summary panel (PROD-2026-05-18-010).
// Centralizing the displayed rating and review count here keeps the
// Cost Calculator hero, /get-quote confirmation, and the new checkout
// trust strip from drifting. Update both values together when the
// numbers refresh; consumers should never hard-code their own copy.
// Source of truth: https://www.trustpilot.com/review/www.languex.co
//
// 2026-05-25 audit — verified against the live Trustpilot page:
//   - Live review count: 1,671 (was hard-coded as 436 here, ~4x stale)
//   - Live rating:        4.8
//
// Product decision (Imran, 2026-05-25): visible marketing copy
// stays at 4.9 — review-collection cadence is fast and the live
// figure is expected to round back up to 4.9 within days. The
// home-page JSON-LD aggregateRating.ratingValue stays at 4.8
// (schema-truthful for Google rich results); only the human-
// visible surfaces use this 4.9 constant. Revisit at the next
// audit if the live Trustpilot number stays at 4.8 for >30 days.
export const TRUSTPILOT_RATING_DISPLAY = "4.9";
export const TRUSTPILOT_REVIEW_COUNT = 1671;
export const TRUSTPILOT_REVIEW_URL =
  "https://www.trustpilot.com/review/www.languex.co";

// Google Business Profile review signals. Mirrors the Trustpilot
// triple above so the two trust sources can be rendered side-by-
// side without duplicating string literals across components.
//
// 2026-05-25 — URL points at the Washington DC location panel,
// jumping directly to the "Reviews" tab via the place-id-anchored
// hash fragment (#mpd=~<placeId>/customers/reviews). Source URL
// (Imran): https://www.google.com/search?q=languex+washington+dc
//   #mpd=~5277046834517868037/customers/reviews
// Session-specific params (sca_esv, sxsrf, ei, screen dims, etc.)
// stripped so the link doesn't decay over time.
//
// COUNT + RATING are still null pending Imran's confirmation.
// Consumer components degrade gracefully: when URL is set but
// count/rating are not, the box renders as a clickable "View
// our reviews on Google" tile (no fabricated numbers). When all
// three are populated, the box flips to the full "Excellent /
// 5.0 / Based on N reviews" presentation that mirrors Trustpilot.
export const GOOGLE_REVIEW_URL: string | null =
  "https://www.google.com/search?q=languex+washington+dc#mpd=~5277046834517868037/customers/reviews";
export const GOOGLE_REVIEW_COUNT: number | null = null;
export const GOOGLE_RATING_DISPLAY: string | null = null;

/**
 * Standard translation cost: max($25, words * $0.10). Floor only applies
 * when there's at least one word — zero-word orders cost $0 (nothing
 * to translate). FUTURE: this is where language-tier multipliers will
 * plug in once the tier table is approved.
 */
export const calculateStandardTranslationCost = (wordCount: number): number => {
  const words = Math.max(0, Number(wordCount) || 0);
  if (words <= 0) return 0;
  return Math.max(
    STANDARD_TRANSLATION_MIN_USD,
    words * TRANSLATION_RATES.standard
  );
};

/**
 * Standard add-on cost: max($10, words * ratePerWord). Floor only applies
 * when there's at least one word, mirroring the parent line.
 */
export const calculateStandardAddonCost = (
  ratePerWord: number,
  wordCount: number
): number => {
  const words = Math.max(0, Number(wordCount) || 0);
  if (words <= 0) return 0;
  return Math.max(STANDARD_ADDON_MIN_USD, words * ratePerWord);
};

export const STANDARD_ORDER_DELIVERY_OPTIONS = [
  {
    value: DELIVERY_OPTIONS_STANDARD.expeditedDelivery,
    label: "Expedited Delivery",
    rate: DELIVERY_RATES_STANDARD.expeditedDelivery,
  },
  {
    value: DELIVERY_OPTIONS_STANDARD.desktopPublishing,
    label: "Desktop Publishing",
    rate: DELIVERY_RATES_STANDARD.desktopPublishing,
  },
  {
    value: DELIVERY_OPTIONS_STANDARD.proofreading,
    label: "Proofreading",
    rate: DELIVERY_RATES_STANDARD.proofreading,
  },
];

export const DELIVERY_OPTIONS_CERTIFIED = {
  notarization: "notarization",
  mailHardCopy: "mailHardCopy",
  expeditedTurnaround: "expeditedTurnaround",
  desktopPublishing: "desktopPublishing",
  proofreading: "proofreading",
} as const;

// Notarization matches the canonical Stripe price ($19.95).
// PROD-2026-05-18-007.
export const DELIVERY_RATES_CERTIFIED = {
  notarization: 19.95,
  mailHardCopy: 20,
  expeditedTurnaround: 10,
} as const;

export const CERTIFIED_ORDER_DELIVERY_OPTIONS = [
  {
    value: DELIVERY_OPTIONS_CERTIFIED.notarization,
    label: "Notarization",
    rate: DELIVERY_RATES_CERTIFIED.notarization,
  },
  {
    value: DELIVERY_OPTIONS_CERTIFIED.mailHardCopy,
    label: "Mailed Hard Copy",
    rate: DELIVERY_RATES_CERTIFIED.mailHardCopy,
  },
  {
    value: DELIVERY_OPTIONS_CERTIFIED.expeditedTurnaround,
    label: "Expedited Turnaround",
    rate: DELIVERY_RATES_CERTIFIED.expeditedTurnaround,
  },
];

export const DELIVERY_OPTIONS = {
  ...DELIVERY_OPTIONS_STANDARD,
  ...DELIVERY_OPTIONS_CERTIFIED,
} as const;

export const DELIVERY_OPTION_VALUES = Object.values(DELIVERY_OPTIONS);

// NOTE: Step Four Payment

export const PAYMENT_METHOD_OPTIONS = {
  card: "card",
  googlePay: "googlePay",
  applePay: "applePay",
  payPal: "payPal",
} as const;

export const PAYMENT_METHOD_OPTION_VALUES = Object.values(
  PAYMENT_METHOD_OPTIONS
);

export const ACCOUNT_CREATION_OPTIONS = {
  create: "create",
  guest: "guest",
  isLoggedIn: "isLoggedIn",
} as const;

export const ACCOUNT_CREATION_OPTION_VALUES = Object.values(
  ACCOUNT_CREATION_OPTIONS
);
